import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildResultSummary, buildSubjectTopicBreakdown } from '../utils/resultSummary';
import { trackButtonClick, trackEvent } from '../analytics';
import { LockOutlined, CheckCircleOutlined, CancelOutlined } from '../icons';
import DashboardTopbar from '../components/DashboardTopbar';
import UpgradeCTA, { HOTMART_URL } from '../components/UpgradeCTA';
import QuestionNavigator from '../components/QuestionNavigator/QuestionNavigator';
import SubjectPerformancePanel from '../components/SubjectPerformancePanel/SubjectPerformancePanel';
import QuestionBlock, { QuestionOption } from '../components/QuestionBlock/QuestionBlock';
import { isValidOptionText } from '../utils/questionOptions';
import { FREE_MOCK_ID } from '../planLimits';
import PageLoader from '../components/PageLoader';
import './SuccessPage.css';

const apiUrl = process.env.REACT_APP_API_URL;
const FULL_EXPLANATION_COUNT = 2;
const ACERTO_GOAL_PERCENT = 60;
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'];

function SuccessPage() {
  const [result, setResult] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewIndex, setReviewIndex] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const { mock } = location.state || {};
  const summary = useMemo(() => buildResultSummary(result), [result]);
  const subjectBreakdown = useMemo(() => buildSubjectTopicBreakdown(result), [result]);

  useEffect(() => {
    if (!mock?.id) {
      navigate('/simulados');
      return;
    }

    const fetchData = async () => {
      const mockId = mock.id;
      setLoading(true);
      try {
        const response = await axios.post(apiUrl + "result", {
          mockId
        });
        setResult(response.data);
        setReviewIndex(0);
        if (mockId === FREE_MOCK_ID) {
          trackEvent('success_page_free_mock_shown');
        }
      } catch (error) {
        alert("Erro ao carregar o resultado. Tente novamente mais tarde.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [mock, navigate]);

  const getQuestionImageSrc = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) return imageUrl;
    return `${apiUrl}uploads/${imageUrl}`;
  };

  const handleRequestPerformance = async () => {
    try {
      const message = [
        'Solicitação de desempenho do aluno',
        '',
        `Acertos: ${summary.overall.correct}/${summary.overall.total}`,
        `Percentual: ${summary.overall.percentage}%`,
        '',
        'Por matéria:'
      ];

      summary.bySubject.forEach((subject) => {
        message.push(`- ${subject.subjectName}: ${subject.correct}/${subject.total} (${subject.percentage}%)`);
      });

      await axios.post(`${apiUrl}send-performance-email`, {
        message: message.join('\n'),
      });

    } catch (error) {
      alert('Não foi possível enviar o pedido no momento. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="App dashboard-page results-page">
        <PageLoader message="Carregando o resultado..." />
      </div>
    );
  }

  const totalQuestions = result.length;
  const answeredCount = result.filter((q) => q.choiceSelected != null && q.choiceSelected >= 0).length;
  const isFreeMock = mock && mock.id === FREE_MOCK_ID;
  const goalReached = summary.overall.percentage >= ACERTO_GOAL_PERCENT;

  const currentQuestion = result[reviewIndex];
  const correctIndex = currentQuestion ? OPTION_LETTERS.indexOf(currentQuestion.correct) : -1;
  const explanationUnlocked = !isFreeMock || reviewIndex < FULL_EXPLANATION_COUNT;
  const isFirst = reviewIndex === 0;
  const isLast = reviewIndex === totalQuestions - 1;

  const goToQuestion = (index) => setReviewIndex(index);
  const goPrev = () => setReviewIndex((i) => Math.max(0, i - 1));
  const goNext = () => setReviewIndex((i) => Math.min(totalQuestions - 1, i + 1));

  return (
    <div className="App dashboard-page results-page">
      <DashboardTopbar
        label={mock?.name ? `${mock.name} — concluído` : 'Resultado do simulado'}
      />

      <div className="results-title-row">
        <div className="results-title-block">
          <h1 className="results-title">Resultado{mock?.name ? ` — ${mock.name}` : ' do simulado'}</h1>
          <span className="results-subtitle">Veja seu rendimento por matéria e revise cada questão comentada abaixo.</span>
        </div>
        <button onClick={() => navigate('/simulados')} className="results-back-btn">
          Meus simulados
        </button>
      </div>

      {isFreeMock && (
        <div className="venda-wrapper">
          <a
            className="modal-button whatsapp"
            href="https://wa.me/555596507899?text=Olá%2C%20gostaria%20de%20solicitar%20meu%20desempenho."
            target="_blank"
            rel="noopener noreferrer"
            onClick={async (event) => {
              event.preventDefault();
              window.open(
                'https://wa.me/555596507899?text=Olá%2C%20gostaria%20de%20solicitar%20meu%20desempenho.',
                '_blank',
                'noopener,noreferrer'
              );
              await handleRequestPerformance();
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.373 0 0 5.373 0 12c0 2.116.553 4.178 1.6 6.02L0 24l6.097-1.586A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12 0-3.204-1.249-6.207-3.48-8.52z" fill="#25D366" />
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.672.15-.198.297-.767.967-.94 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.173.198-.297.297-.495.099-.198.05-.372-.025-.52-.074-.148-.672-1.617-.921-2.219-.242-.583-.487-.504-.672-.513l-.572-.01c-.198 0-.52.074-.793.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487 2.982 1.288 2.982.858 3.518.806.536-.049 1.758-.718 2.005-1.412.248-.695.248-1.289.173-1.412-.074-.124-.273-.198-.57-.347z" fill="#fff" />
            </svg>
              Enviar meu desempenho por WhatsApp
          </a>
        </div>
      )}

      <div className="kpi-strip results-kpi-strip">
        <div className="kpi-card kpi-card--primary">
          <span className="kpi-label">QUESTÕES RESPONDIDAS</span>
          <span className="kpi-value">{answeredCount} <span className="kpi-value-suffix">de {totalQuestions}</span></span>
        </div>
        <div className={`kpi-card ${goalReached ? 'kpi-card--good' : 'kpi-card--exam'}`}>
          <span className="kpi-label">APROVEITAMENTO GERAL</span>
          <span className="kpi-value">{summary.overall.percentage}%</span>
          <span className="kpi-sub">{goalReached ? 'acima' : 'abaixo'} da meta de {ACERTO_GOAL_PERCENT}%</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">ACERTOS</span>
          <span className="kpi-value">{summary.overall.correct} <span className="kpi-value-suffix">de {summary.overall.total}</span></span>
        </div>
      </div>

      <SubjectPerformancePanel data={subjectBreakdown} />

      {isFreeMock && (
        <UpgradeCTA
          variant="inline"
          trackingPrefix="success_page_top"
          title="Gostou do resultado? Isso foi só uma amostra."
          subtitle="Continue estudando com acesso completo até a prova da Polícia Penal RS."
        />
      )}

      <div className="results-correction">
        <h2 className="results-correction-title">Correção detalhada</h2>

        <div className="results-correction-layout">
          <QuestionNavigator
            className="result-nav-panel"
            title="Navegador"
            subtitle={`${summary.overall.correct} de ${summary.overall.total} corretas`}
            count={totalQuestions}
            currentIndex={reviewIndex}
            onSelect={goToQuestion}
            getStatus={(index) => (result[index]?.isCorrect ? 'correct' : 'incorrect')}
            legend={[
              { status: 'current', label: 'Questão atual' },
              { status: 'correct', label: 'Acertou' },
              { status: 'incorrect', label: 'Errou' },
            ]}
          />

          {currentQuestion && (
            <div className="result-question-panel">
              <QuestionBlock
                counterText={`Questão ${reviewIndex + 1} de ${totalQuestions}`}
                headerExtra={(
                  <span className={`result-status-badge ${currentQuestion.isCorrect ? 'correct' : 'incorrect'}`}>
                    {currentQuestion.isCorrect ? (
                      <><CheckCircleOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Você acertou</>
                    ) : (
                      <><CancelOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Você errou</>
                    )}
                  </span>
                )}
                subjectName={currentQuestion.subjectName}
                title={currentQuestion.title}
                imageSrc={currentQuestion.image_url ? getQuestionImageSrc(currentQuestion.image_url) : null}
                imageAlt={`Imagem da questão ${reviewIndex + 1}`}
              >
                {currentQuestion.choices.map((option, optionIndex) => {
                  if (!isValidOptionText(option)) {
                    return null;
                  }
                  const isCorrectChoice = optionIndex === correctIndex;
                  const isSelectedChoice = optionIndex === currentQuestion.choiceSelected;
                  const tag = isSelectedChoice && isCorrectChoice
                    ? 'Sua resposta · Correta'
                    : isSelectedChoice
                      ? 'Sua resposta'
                      : isCorrectChoice
                        ? 'Correta'
                        : null;
                  return (
                    <QuestionOption
                      key={optionIndex}
                      index={optionIndex}
                      letter={OPTION_LETTERS[optionIndex]}
                      text={option}
                      status={isCorrectChoice ? 'correct' : (isSelectedChoice ? 'wrong' : '')}
                      tag={tag}
                    />
                  );
                })}
              </QuestionBlock>

              <div className="result-comment-box">
                <span className="result-comment-title">Comentário do professor</span>
                {explanationUnlocked ? (
                  <span className="result-comment-text" style={{ whiteSpace: 'pre-line' }}>{currentQuestion.explication}</span>
                ) : (
                  <div className="explication-locked">
                    <div className="explication-locked-teaser">{currentQuestion.explication}</div>
                    <a
                      className="explication-unlock-btn"
                      href={HOTMART_URL}
                      onClick={() => trackButtonClick('explicacao_bloqueada_click', { questionIndex: reviewIndex })}
                    >
                      <LockOutlined style={{ fontSize: 15, verticalAlign: 'middle', marginRight: 6 }} />
                      Desbloquear explicação completa
                    </a>
                  </div>
                )}
              </div>

              <div className="result-nav-buttons">
                <button type="button" onClick={goPrev} disabled={isFirst}>← Anterior</button>
                {isLast ? (
                  <button type="button" className="primary" onClick={() => navigate('/simulados')}>Concluir revisão</button>
                ) : (
                  <button type="button" className="primary" onClick={goNext}>Próxima →</button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isFreeMock && (
        <UpgradeCTA
          variant="inline"
          trackingPrefix="success_page_bottom"
          title="Quero acesso completo até a prova"
          subtitle="Libere todos os simulados, apostilas e explicações completas até a data da prova."
        />
      )}

      <button onClick={() => navigate('/simulados')} className="results-back-btn results-back-btn--bottom">
        Meus simulados
      </button>
    </div>
  );
}

export default SuccessPage;
